import { useState, useEffect, useCallback } from 'react';
import {
  universitiesApi,
  University,
  CreateUniversityDto,
  DirectionsResponse,
  StudyForm,
  StudyLanguage,
} from '../../../api/universities';
import { toast } from 'sonner';
import { UseUniversitiesReturn } from '../types';

const initialFormData: CreateUniversityDto = {
  name: '',
  slug: '',
  location: '',
  phone: '',
  website: '',
  region: '',
};

export function useUniversities(): UseUniversitiesReturn {
  const [universities, setUniversities] = useState<University[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [regions, setRegions] = useState<string[]>([]);

  // Year
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  // University CRUD
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState<University | null>(null);
  const [formData, setFormData] = useState<CreateUniversityDto>(initialFormData);

  // Directions
  const [isDirectionsDialogOpen, setIsDirectionsDialogOpen] = useState(false);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [directionsData, setDirectionsData] = useState<DirectionsResponse | null>(null);
  const [isDirectionsLoading, setIsDirectionsLoading] = useState(false);
  const [directionsForm, setDirectionsForm] = useState<StudyForm | null>(null);
  const [directionsLanguage, setDirectionsLanguage] = useState<StudyLanguage | null>(null);

  const fetchUniversities = useCallback(async () => {
    setIsLoading(true);
    try {
      const params: any = { page: currentPage, limit: 20 };
      if (searchQuery) params.search = searchQuery;
      if (regionFilter) params.region = regionFilter;
      if (selectedYear) params.year = selectedYear;
      const response = await universitiesApi.getAll(params);
      setUniversities(response.data);
      setTotal(response.total);
      setTotalPages(Math.ceil(response.total / 20));
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to fetch universities');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, searchQuery, regionFilter, selectedYear]);

  const fetchRegions = useCallback(async () => {
    try {
      const data = await universitiesApi.getRegions();
      setRegions(data);
    } catch {
      // silent
    }
  }, []);

  const fetchYears = useCallback(async () => {
    try {
      const data = await universitiesApi.getYears();
      setAvailableYears(data);
      if (data.length > 0 && !data.includes(selectedYear)) {
        setSelectedYear(data[0]);
      }
    } catch {
      // silent
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUniversities();
  }, [fetchUniversities]);

  useEffect(() => {
    fetchRegions();
    fetchYears();
  }, [fetchRegions, fetchYears]);

  // ─── University CRUD ───────────────────────────────────────────────

  const handleCreate = async () => {
    try {
      await universitiesApi.create(formData);
      toast.success('University created successfully');
      setIsCreateDialogOpen(false);
      setFormData(initialFormData);
      fetchUniversities();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create university');
    }
  };

  const handleEdit = (university: University) => {
    setEditingUniversity(university);
    setFormData({
      name: university.name,
      name_ru: university.name_ru || '',
      name_kiril: university.name_kiril || '',
      slug: university.slug,
      location: university.location || '',
      phone: university.phone || '',
      website: university.website || '',
      region: university.region || '',
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!editingUniversity) return;
    try {
      await universitiesApi.update(editingUniversity.id, formData);
      toast.success('University updated successfully');
      setIsEditDialogOpen(false);
      setEditingUniversity(null);
      fetchUniversities();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to update university');
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this university?')) return;
    try {
      await universitiesApi.delete(id);
      toast.success('University deleted successfully');
      fetchUniversities();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to delete university');
    }
  };

  // ─── Directions ────────────────────────────────────────────────────

  const fetchDirections = useCallback(
    async (universityId: string) => {
      setIsDirectionsLoading(true);
      try {
        const params: any = { year: selectedYear };
        if (directionsForm) params.form = directionsForm;
        if (directionsLanguage) params.language = directionsLanguage;
        const data = await universitiesApi.getDirections(universityId, params);
        setDirectionsData(data);
      } catch (error: any) {
        toast.error(error.response?.data?.message || 'Failed to fetch directions');
      } finally {
        setIsDirectionsLoading(false);
      }
    },
    [selectedYear, directionsForm, directionsLanguage],
  );

  const handleViewDirections = (university: University) => {
    setSelectedUniversity(university);
    setIsDirectionsDialogOpen(true);
    fetchDirections(university.id);
  };

  // Refetch when filters inside dialog change.
  useEffect(() => {
    if (isDirectionsDialogOpen && selectedUniversity) {
      fetchDirections(selectedUniversity.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directionsForm, directionsLanguage, selectedYear]);

  return {
    universities,
    isLoading,
    currentPage,
    totalPages,
    total,
    searchQuery,
    regionFilter,
    regions,
    availableYears,
    selectedYear,
    setSelectedYear,
    isCreateDialogOpen,
    isEditDialogOpen,
    formData,
    setIsCreateDialogOpen,
    setIsEditDialogOpen,
    setFormData,
    setSearchQuery,
    setRegionFilter,
    setCurrentPage,
    handleCreate,
    handleEdit,
    handleUpdate,
    handleDelete,
    isDirectionsDialogOpen,
    selectedUniversity,
    directionsData,
    isDirectionsLoading,
    directionsForm,
    directionsLanguage,
    setDirectionsForm,
    setDirectionsLanguage,
    handleViewDirections,
    setIsDirectionsDialogOpen,
  };
}
